import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Row, Col, Button, Collapse } from 'react-bootstrap';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import DateFilterInput from '../Utils/DateFilterInput';
import PersonInput from '../Utils/PersonInput';
import ProductInput from '../Utils/ProductInput';
import IntegerInput from '../Utils/IntegerInput';
import InputField from '../Utils/InputField';
import { getCurrentDate, convertDateToISO, getCurrentDateMinusDays } from '../Utils/utils';

const RomaneiosFilter = ({ onFilter, onClear }) => {
  const { handleSubmit, control, reset, watch, formState: { errors }, register, setValue } = useForm({
    defaultValues: {
      initialDate: getCurrentDateMinusDays(3),
      finalDate: getCurrentDate(),
      oldId: '',
      licensePlate: '',
      driver: '',
      person: '',
      product: '',
      entry: true,
      exit: true,
      entryFinished: true,
      exitFinished: true,
      active: false,
    },
  });

  const [open, setOpen] = useState(true);
  
  const handleFilter = (data) => {
    const getSafeDate = (dateStr, fallback) => {
      if (!dateStr || dateStr.trim() === '') {
        return fallback;
      }
      const isValid = /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);
      return isValid ? convertDateToISO(dateStr) : fallback;
    };
  
    const defaultInitialDate = getCurrentDateMinusDays(30);
    const defaultFinalDate = getCurrentDate();
  
    onFilter({
      oldId: data.oldId || undefined,
      licensePlate: data.licensePlate || undefined,
      driver: data.driver?.id || null,
      person: data.person?.id || null,
      product: data.product?.id || null,
      initialDate: getSafeDate(data.initialDate, defaultInitialDate),
      finalDate: getSafeDate(data.finalDate, defaultFinalDate),
      entry: data.entry,
      exit: data.exit,
      entryFinished: data.entryFinished,
      exitFinished: data.exitFinished,
      active: !data.active,
    });
  };  

  const handleClear = () => {
    reset();
    onClear();
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <div
      style={{
        border: open ? '1px solid black' : 'none',
        borderRadius: '5px',
        padding: open ? '10px' : '0px',
        marginBottom: '20px',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <h6
        className="text-center mb-3 d-flex align-items-center justify-content-center"
        style={{ cursor: 'pointer' }}
        onClick={handleToggle}
      >
        Filtros Avançados{' '}
        {open ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
      </h6>
      <Collapse in={open}>
        <div>
          <Form onSubmit={handleSubmit(handleFilter)}>
            <Row className="mb-0">
              <Col md={1}>
                <Controller
                  name="oldId"
                  control={control}
                  render={({ field }) => (
                    <IntegerInput
                      label="Código"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Código"
                      userRoles={false}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="initialDate"
                  control={control}
                  render={({ field }) => (
                    <DateFilterInput
                      label="Data Inicial"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.initialDate?.message}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="finalDate"
                  control={control}
                  render={({ field }) => (
                    <DateFilterInput
                      label="Data Final"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.finalDate?.message}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="licensePlate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputField
                      label="Placa"
                      name="licensePlate"
                      register={register}
                      errors={errors}
                      placeholder="Placa"
                      value={watch('licensePlate') || ''}
                    />
                  )}
                />
              </Col>
              <Col md={5}>
                <Controller
                  name="driver"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={false}
                      onChange={(selected) => setValue('driver', selected)}
                      onCreated={(created) => {
                        setValue('driver', { id: created.id, name: created.name });
                      }}
                      label="Motorista"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      autoComplete='off'
                    />
                  )}
                />
              </Col>
            </Row>
            <Row className='mb-0'>
              <Col md={6}>
                <Controller
                  name="person"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={false}
                      onChange={(selected) => setValue('person', selected)}
                      onCreated={(created) => {
                        setValue('person', { id: created.id, name: created.name });
                      }}
                      label="Produtor / Cliente"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      autoComplete='off'
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="product"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      createdNew={false}
                      onChange={(selected) => setValue('product', selected)}
                      onCreated={(created) => {
                        setValue('product', { id: created.id, name: created.name });
                      }}
                      label="Produto / Cultura"
                      isInvalid={!!fieldState.error}
                      autoComplete='off'
                    />
                  )}
                />
              </Col>
            </Row>
            <Row className="mb-0">
              <Col md={10}>
                <Form.Group
                  controlId="status"
                  style={{ border: 'solid #dee2e6 1px', padding: '5px 15px' }}
                  className="mb-3"
                >
                  <Form.Label className="center">Situação</Form.Label>
                  <Row>
                    <Col md={3}>
                      <Controller
                        name="entry"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="checkbox"
                            label="Entrada Suspenso"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Col>
                    <Col md={3}>
                      <Controller
                        name="exit"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="checkbox"
                            label="Saída Suspenso"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Col>
                    <Col md={3}>
                      <Controller
                        name="entryFinished"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="checkbox"
                            label="Entrada Finalizada"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Col>
                    <Col md={3}>
                      <Controller
                        name="exitFinished"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="checkbox"
                            label="Saída Finalizada"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Form.Group
                      controlId="status"
                      style={{ border: 'solid #dee2e6 1px', padding: '5px 15px' }}
                      className="mb-3"
                    >
                      <Form.Label className="center">Status</Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Estornado"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </Form.Group>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="d-flex justify-content-start">
                <Button
                  variant="primary"
                  type="submit"
                  className="button-custom d-flex align-items-center me-2"
                >
                  <FaSearch className="me-2" /> Pesquisar
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleClear}
                  className="d-flex align-items-center"
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Collapse>
    </div>
  );
};

export default RomaneiosFilter;